import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  OrderHistoryHeader,
  OrderFilterTabs,
  OrderHistoryEmptyState,
  OrderHistoryCard,
  orderHistoryTranslations,
} from './order-history';
import type { FilterType, OrderHistoryItem } from './order-history';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { SessionOrdersSheet } from '@/components/session';
import { payNowTranslations } from '@/i18n/paymentModal';
import { checkoutTranslations } from '@/i18n';
import { hasPayableSessionLines, useSessionUnpaidPostpay } from '@/hooks/useSessionUnpaidPostpay';
import { ROUTES } from '@/config/routes';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';

const NOTICE_DISMISS_MS = 5000;

/**
 * β-3b: 후불 (postpay) / 선불 수기 (prepay_manual) 결제 완료 시 CheckoutPage 가
 * `?notice=postpay` 또는 `?notice=prepay_manual` 로 navigate. 배너 5초 노출 후
 * query 자동 제거 (새로고침 시 재표시 방지).
 */
type NoticeType = 'postpay' | 'prepay_manual';

function parseNotice(value: string | null): NoticeType | null {
  return value === 'postpay' || value === 'prepay_manual' ? value : null;
}

// 타입 재익스포트 (외부에서 사용 가능하도록)
export type { OrderHistoryItem, OrderHistoryPageProps } from './order-history';

interface OrderHistoryPageComponentProps {
  onBack: () => void;
  orders: OrderHistoryItem[];
  onViewReceipt: (order: OrderHistoryItem) => void;
}

// URL 쿼리 파라미터에서 필터 타입 파싱
function parseFilterFromUrl(tabParam: string | null): FilterType {
  if (tabParam === 'pickup' || tabParam === 'table') {
    return tabParam;
  }
  return 'all';
}

export function OrderHistoryPage({
  onBack,
  orders,
  onViewReceipt,
}: OrderHistoryPageComponentProps) {
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const { guestCredentials } = useOrderHistory();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // #603 [3]: 세션 주문 시트(라인 현황) 오픈 여부.
  const [isSessionSheetOpen, setIsSessionSheetOpen] = useState(false);

  // URL에서 탭 상태 파생
  const selectedFilter = useMemo(() => {
    return parseFilterFromUrl(searchParams.get('tab'));
  }, [searchParams]);

  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const t = orderHistoryTranslations[locale];
  const tCheckout = checkoutTranslations[locale];

  // β-3b: ?notice=postpay|prepay_manual 배너
  const notice = useMemo(() => parseNotice(searchParams.get('notice')), [searchParams]);
  const noticeMessage = useMemo(() => {
    if (notice === 'postpay') return tCheckout.postpayNotice;
    if (notice === 'prepay_manual') return tCheckout.prepayManualNotice;
    return null;
  }, [notice, tCheckout]);

  // 배너 노출 5초 후 query 제거 — 새로고침 시 재표시 방지
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('notice');
        return next;
      }, { replace: true });
    }, NOTICE_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notice, setSearchParams]);

  // 탭 변경 시 URL 업데이트
  const handleFilterChange = useCallback((filter: FilterType) => {
    if (filter === 'all') {
      // 'all'이면 쿼리 파라미터 제거
      setSearchParams({});
    } else {
      setSearchParams({ tab: filter });
    }
  }, [setSearchParams]);

  const filteredOrders = useMemo(() => {
    return orders
      .filter((order) => {
        if (selectedFilter === 'all') return true;
        return order.type === selectedFilter;
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [orders, selectedFilter]);

  /**
   * 후불 미결제 주문이 목록에 하나라도 있는지 — 세션 합산 조회 진입 게이트 (R8).
   *
   * 주문내역 전체(필터 무관) 기준. 하나도 없으면 `unpaid-postpay` 호출 자체를 생략한다
   * (불필요한 API 호출 회피). `OrderHistoryCard.isPostpayUnpaid` 와 동일 기준(SSOT).
   */
  const hasLocalPostpayUnpaid = useMemo(
    () =>
      orders.some(
        (order) =>
          order.latestPayment?.timing === 'postpay' &&
          order.latestPayment?.status === 'pending',
      ),
    [orders],
  );

  /**
   * 세션 미결제 후불 조회 (R8 → #603 [2][3] 공유 훅) — 메뉴 화면([2]) 과 동일 출처.
   *
   * 후불 미결제 주문이 목록에 있을 때만(`enabled=hasLocalPostpayUnpaid`) admin R5 호출한다.
   * fail-closed: 토큰 부재 → `tokenMissing`(복구 안내) / 0건·orders 부재·조회 실패 → `data` null(미표시).
   * 회원이면 guest_password 미전송(GET/페이지/POST 정합). 인라인 effect → useSessionUnpaidPostpay 추출.
   */
  const {
    data: sessionSummary,
    tokenMissing: sessionTokenMissing,
  } = useSessionUnpaidPostpay({
    enabled: hasLocalPostpayUnpaid,
    isLoggedIn,
    guestPassword: isLoggedIn ? undefined : guestCredentials?.password,
  });

  // #654 codex I3: 시트 CTA 와 동일한 결제 진입 판정(SSOT) — 빈 items(계약 위반) 시
  // 시트는 CTA 를 숨기는데 배너 "전체 결제"만 sessionSummary !== null 로 직접 열리면 불일치.
  const sessionHasPayableLines = hasPayableSessionLines(sessionSummary);

  // #603 [4] → #661: 세션 "전체 결제" — 전용 페이지(/table-session/charge)로 정상 라우트 push.
  // 모달 히스토리 조작 없음(PR #622/#621 /cart 함정 회피) — 뒤로가기 시 주문내역 자연 복귀.
  // I3: 결제 가능 라인 없으면 no-op (배너 CTA 숨김과 이중 방어 — fail-closed, 페이지도 재검사).
  const handleSessionChargeOpen = useCallback(() => {
    if (!sessionHasPayableLines) return;
    navigate(ROUTES.TABLE_SESSION_CHARGE);
  }, [sessionHasPayableLines, navigate]);

  // #603 [3]: 세션 주문 시트 열기/닫기 + 시트 → 전체결제 페이지 진입.
  const handleSessionSheetOpen = useCallback(() => {
    setIsSessionSheetOpen(true);
  }, []);

  const handleSessionSheetClose = useCallback(() => {
    setIsSessionSheetOpen(false);
  }, []);

  const handleSessionSheetCharge = useCallback(() => {
    setIsSessionSheetOpen(false);
    handleSessionChargeOpen();
  }, [handleSessionChargeOpen]);

  const payNowT = payNowTranslations[locale];

  // #603 [3]: 시트 헤더 테이블 번호 — 테이블 주문 중 첫 건의 tableNumber(표시용).
  const sessionTableNumber = useMemo(
    () => orders.find((order) => order.type === 'table' && order.tableNumber)?.tableNumber ?? null,
    [orders],
  );

  // #603 [3]: 세션 주문 시트 라벨 (payNow.session 키).
  const sessionSheetLabels = useMemo(
    () => ({
      title: payNowT.session.sheetTitle,
      totalLabel: payNowT.session.sheetTotalLabel,
      payButton: payNowT.session.sheetPayButton,
      empty: payNowT.session.sheetEmpty,
      closeLabel: payNowT.session.sheetClose,
    }),
    [payNowT.session],
  );

  /**
   * #603 [4] append 정합: "세션 결제" 배너 노출 조건 = 미결제 후불 주문서 존재.
   *
   * 기존 `count>=2` 트리거는 append 모델(미결제 = Open 주문서 1건 → count 항상 1)에서
   * 영영 미표시(dead path)였다(§0(B)). 트리거를 "sessionSummary 존재"로 재정의하고,
   * 단건 "지금 결제"는 세션 결제로 수렴(제거)했다. 0건/조회 실패/orders 부재는
   * sessionSummary === null 이라 자연 배제(fail-closed).
   *
   * #654 codex I3 R2 — 의도된 비대칭 (SSOT 우회 아님):
   *   배너 **표시**는 의도적으로 `sessionSummary !== null` 만 본다 — 빈 items(계약 위반)
   *   상태에서도 요약 + "주문 현황 보기"(→ 시트 빈 안내)를 남겨 이상 상태를 사용자에게
   *   노출한다(무 CTA 침묵 회피). 반면 **결제 진입 요소는 전부 SSOT(sessionHasPayableLines
   *   = hasPayableSessionLines) 게이팅**: "전체 결제" CTA 렌더 조건 + handleSessionChargeOpen
   *   no-op 가드 + 페이지(SessionChargePage) 자체 fail-closed redirect 3중(#661). 배너 내 결제 진입 요소는 "전체 결제"
   *   버튼 단 하나다(배너 래퍼/전 영역 클릭 없음, "주문 현황 보기"는 시트 표시만 —
   *   시트 CTA 는 다시 같은 SSOT 판정으로 숨김).
   */
  const showSessionBanner = sessionSummary !== null;

  /**
   * #603 [4] Codex Critical: 세션 토큰 부재 복구 안내 노출 조건.
   *
   * 세션 결제 CTA(배너)와 배타 — 배너가 뜨면 정상 결제 가능하므로 복구 안내 불필요.
   * sessionTokenMissing 은 effect 에서 "미결제 후불 존재 + 토큰 부재" 일 때만 true 가 되므로,
   * 미결제 후불이 없으면 자연히 false(불필요 노출 차단).
   */
  const showRecoveryNotice = sessionTokenMissing && !showSessionBanner;

  const sessionBannerMessage = useMemo(() => {
    if (!sessionSummary) return null;
    return payNowT.session.bannerSummary
      .replace('{count}', String(sessionSummary.count))
      .replace('{amount}', formatCurrencyUtil(Number(sessionSummary.totalAmount), currency));
  }, [sessionSummary, payNowT.session.bannerSummary, currency]);

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <OrderHistoryHeader onBack={onBack} translations={t} />

      {/* β-3b: 결제 완료 안내 배너 (5초 후 자동 숨김) */}
      {noticeMessage && (
        <div
          role="status"
          aria-live="polite"
          data-testid="order-notice-banner"
          className="mx-4 mt-3 rounded-md border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary-foreground"
        >
          {noticeMessage}
        </div>
      )}

      {/* #603 [4]: 세션 결제 요약 배너 (미결제 후불 주문서 존재 + 세션 토큰 정상) */}
      {showSessionBanner && sessionBannerMessage && (
        <div
          data-testid="session-pay-banner"
          className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-3"
        >
          <span className="text-sm font-medium text-orange-800">{sessionBannerMessage}</span>
          <div className="flex shrink-0 items-center gap-2">
            {/* #603 [3]: 주문 현황 보기 → 세션 주문 시트(라인 전체) */}
            <button
              type="button"
              data-testid="session-view-orders-button"
              onClick={handleSessionSheetOpen}
              className="rounded-[5px] border border-orange-300 px-3 py-2 text-sm font-medium text-orange-800 transition-colors hover:bg-orange-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
            >
              {payNowT.session.sheetTitle}
            </button>
            {/* #654 codex I3: 결제 가능 라인 없으면(빈 items 계약 위반) CTA 숨김 — 시트와 동일 fail-closed.
                "주문 현황 보기"는 유지해 빈 안내로 이상 상태를 노출한다(무 CTA 침묵 회피). */}
            {sessionHasPayableLines && (
              <button
                type="button"
                data-testid="session-pay-all-button"
                onClick={handleSessionChargeOpen}
                className="rounded-[5px] bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {payNowT.session.payAllButton}
              </button>
            )}
          </div>
        </div>
      )}

      {/* #603 [4] Codex Critical: 미결제 후불 있으나 세션 토큰 부재 → 결제 도달성 회복 복구 안내.
          단건결제 부활 대신 테이블 QR 재스캔을 유도(무 CTA 침묵 차단). */}
      {showRecoveryNotice && (
        <div
          role="status"
          aria-live="polite"
          data-testid="session-recovery-notice"
          className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900"
        >
          {payNowT.session.recoveryNotice}
        </div>
      )}

      {/* Filter Tabs */}
      <OrderFilterTabs
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
        translations={t}
      />

      {/* Orders List */}
      <div className="px-4 pb-[96px] pt-[20px] pr-[16px] pl-[16px]">
        {filteredOrders.length === 0 ? (
          <OrderHistoryEmptyState translations={t} />
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => (
              <OrderHistoryCard
                key={order.id}
                order={order}
                translations={t}
                onViewReceipt={onViewReceipt}
                /* #603 [4]: per-order 단건 "지금 결제" 는 세션 결제로 수렴(제거) — onPayNow 미주입.
                   후불 미결제는 세션 결제 배너로 일원화한다(같은 1 주문서 중복 결제 차단). */
              />
            ))}
          </div>
        )}
      </div>

      {/* #603 [3] → #654: 세션 주문 시트 — 미결제 주문 전체(N건) 주문별 섹션 표시 + 전체결제 진입.
          §0(C): 시간강조 없음(item created_at 부재). 배너 "주문 현황 보기" 로 진입한다. */}
      {sessionSummary && (
        <SessionOrdersSheet
          isOpen={isSessionSheetOpen}
          orders={sessionSummary.orders}
          totalAmount={sessionSummary.totalAmount}
          tableNumber={sessionTableNumber}
          currency={currency}
          labels={sessionSheetLabels}
          onClose={handleSessionSheetClose}
          onCharge={handleSessionSheetCharge}
        />
      )}
    </div>
  );
}
